<template>
  <div>
    <div class="level is-mobile">
      <div class="level-left" />
      <div class="level-right">
        <div class="level-item">
          <b-field class="is-light">
            <b-select
              v-model="revenue"
              placeholder="FILTER BY"
              size="is-small"
            >
              <option value="potentialrevenue">
                Potential Revenue
              </option>

              <option value="weightedrevenue">
                Weighted Revenue
              </option>
            </b-select>
          </b-field>
        </div>

        <div
          v-if="hasRole('ROLE_SALES_MANAGER') || hasRole('ROLE_BUSINESS_ADMIN')"
          class="level-item"
        >
          <b-field class="is-light">
            <b-select
              v-model="selected"
              placeholder="FILTER BY"
              size="is-small"
            >
              <option value="all">
                All
              </option>

              <hr class="dropdown-divider">

              <option value="self">
                Self
              </option>

              <hr class="dropdown-divider">

              <option
                v-for="(subordinate, subordinateIndex) in subordinatesList"
                :key="subordinateIndex"
                :value="subordinate.id"
              >
                {{ subordinate.fullname.trim() }}
              </option>

              <hr class="dropdown-divider">

              <option
                v-for="team in teams"
                :key="team.id"
                :value="`team${team.id}`"
              >
                {{ team.name }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>

    <div class="card is-rounded-card is-spaced-top">
      <header class="card-header">
        <p class="card-header-title">
          {{ revenue === 'potentialrevenue' ? 'potential' : 'weighted' }} revenue by stage
        </p>
      </header>

      <div class="card-content">
        <highcharts
          v-if="hasData"
          :options="weightedRevenueByStageSeries"
        />

        <div
          v-else
          class="level is-mobile"
        >
          <div class="level-item has-text-centered">
            <div>
              <figure class="image">
                <img
                  src="@/assets/images/no-data.svg"
                  alt="No Data"
                >
              </figure>
              <b-button
                type="is-primary"
                size="is-small"
              >
                no data found
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Chart } from 'highcharts-vue';

export default {
  name: 'WeightedRevenueByStage',

  components: {
    highcharts: Chart,
  },

  data: () => ({
    self: localStorage.getItem('user_id'),
    selected: '',
    revenue: 'weightedrevenue',
  }),

  computed: {
    ...mapGetters({
      weightedRevenueByStage: 'Graphs/getWeightedRevenueByStage',
      subordinatesList: 'Users/getSubordinates',
      teams: 'Graphs/getTeams',
    }),

    hasData() {
      const data = [...this.weightedRevenueByStage];
      if (data.length === 0) return false;

      // Check if there's any revenue data (either WeightedRevenue or PotentialRevenue)
      const totalArray = data.map((el) => el.WeightedRevenue || el.PotentialRevenue || 0);
      const total = totalArray.reduce((a, b) => a + b, 0);
      return total > 0;
    },

    weightedRevenueByStageSeries() {
      const data = [...this.weightedRevenueByStage];

      // Map data to use the selected revenue type
      const series = data
        .sort((a, b) => a.orderSequence - b.orderSequence)
        .map((obj) => ({
          name: obj.Name,
          y: this.revenue === 'weightedrevenue' ? obj.WeightedRevenue : obj.PotentialRevenue,
          Total: obj.Total || 0, // Include Total if available
        }));

      const getColorForName = (name) => {
        // Generate a hash from the name
        let hash = 0;
        for (let i = 0; i < name.length; i += 1) {
          hash = name.charCodeAt(i) + hash * 31;
          hash %= 0xffffffff; // Keep the hash within 32-bit range
        }

        // Convert the hash to a hexadecimal color code
        let color = '#';
        for (let i = 0; i < 3; i += 1) {
          const value = Math.floor(Math.abs(hash) / (256 ** i)) % 256;
          color += value.toString(16).padStart(2, '0');
        }

        return color;
      };

      const colors = series.map((item) => getColorForName(item.name));

      const openDrilldown = (type) => {
        const selectedSubordinate = this.subordinatesList.find(
          (subordinate) => subordinate.id === this.selected,
        );
        if (selectedSubordinate) {
          this.$emit('open-drilldown', {
            name: 'opportunities',
            filters: {
              stage: `${type}`,
              owners: this.selected === 'self'
                ? localStorage.getItem('full_name') : selectedSubordinate.fullname,
            },
          });
        } else if (this.selected.toString().startsWith('team')) {
          this.$emit('open-drilldown', {
            name: 'opportunities',
            filters: {
              stage: `${type}`,
              teamId: this.selected.toString().substring(4),
            },
          });
        } else {
          this.$emit('open-drilldown', {
            name: 'opportunities',
            filters: { stage: `${type}` },
          });
        }
      };

      return {
        chart: {
          type: 'pyramid',
          style: {
            fontFamily: 'Open Sans',
          },
        },

        credits: {
          enabled: false,
        },

        title: {
          text: null,
        },

        legend: {
          reversed: false,
        },

        tooltip: {
          pointFormat: '{series.name}: <b>$ {point.y:,.2f}</b> <br>',
          shared: true,
        },

        colors,

        plotOptions: {
          series: {
            minPointLength: 10,

            dataLabels: {
              enabled: true,
              format: '<b>{point.Total}</b> ({point.y:,.0f})',
              softConnector: true,
            },

            allowPointSelect: true,
            point: {
              events: {
                select(event) {
                  openDrilldown(event.target.name);
                },
              },
            },
          },

          pyramid: {
            reversed: false,
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false,
            },
            showInLegend: true,
          },
        },

        series: {
          name: 'amount',
          data: series,
        },

        exporting: {
          buttons: {
            contextButton: {
              menuItems: [
                'printChart',
                'separator',
                'downloadPNG',
                'downloadJPEG',
                'downloadPDF',
                'downloadSVG',
              ],
            },
          },
        },
      };
    },
  },

  watch: {
    selected: {
      handler(value) {
        if (value === 'all') {
          this.$store.dispatch('Graphs/fetchWeightedRevenueByStage', { teamid: '', userid: '' });
        } else if (value === 'self') {
          this.$store.dispatch('Graphs/fetchWeightedRevenueByStage', { teamid: '', userid: this.self });
        } else if (value.toString().startsWith('team')) {
          this.$store.dispatch(
            'Graphs/fetchWeightedRevenueByStage',
            { teamid: value.toString().substring(4), userid: '' },
          );
        } else {
          this.$store.dispatch('Graphs/fetchWeightedRevenueByStage', { teamid: '', userid: value });
        }
      },

      deep: true,
    },
  },

  async created() {
    try {
      if (this.hasRole('ROLE_SALES_PERSON')) {
        await this.$store.dispatch(
          'Graphs/fetchWeightedRevenueByStage',
          { teamid: '', userid: this.self },
        );
      } else {
        await this.$store.dispatch(
          'Graphs/fetchWeightedRevenueByStage',
          { teamid: '', userid: '' },
        );
      }
      this.selected = this.hasRole('ROLE_SALES_PERSON') ? 'self' : 'all';
    } catch (error) {
      console.error(error);
    }
  },

};
</script>

<style>

</style>
